// Copied from coulomb-commons-ui soh-band-migration 0.1.0 (src/components/SohBandMigrationChart.jsx)
// and adapted for samsar-v1: generic "buckets over time" sankey, pre-aggregated
// input (node counts + flows), no entity granularities, no stat-tile filter.
//
// Why d3-sankey: it is a pure layout engine. The SVG is rendered here by hand,
// which gives full control over the DOM (data-node-id / data-edge-source /
// data-edge-target attributes), colours, opacity transitions and events.
//
//   positioning  -> node y0/y1 and ribbon ends come from computeSankeyGeometry
//                   (minimum node / ribbon sizes, legend order) and column x from
//                   computeColumnX (sankeyGeometry.js). d3-sankey's layout pass
//                   always writes its own positions, so they are overwritten after
//                   the pass and sankey.update(graph) recomputes link geometry.
//   ribbons      -> drawn from those ribbon ends (two-cursor stacking), not from
//                   d3-sankey's link generator; a transparent stroke widens the hit
//                   area of thin ribbons.
//   hover/dim    -> mouseenter/mouseleave on nodes and ribbons drive
//                   sankeyInteraction.js, which restyles the SVG elements.
//   tooltip      -> React (<SankeyTooltip />), following the pointer.
//   click        -> click (or Enter / Space) on a node -> onNodeClick({ columnKey, bucketId, ... });
//                   on a ribbon -> onFlowClick({ fromColumnKey, toColumnKey, ... }).
//   keyboard     -> one tab stop (roving tabindex, sankeyFocus.js): arrows / Home / End
//                   move between nodes and ribbons, the focused one gets an outline and
//                   its column is panned into view.
//   labels       -> a count label beside every bar (layoutCountLabels).

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { sankey as d3Sankey, sankeyLeft } from "d3-sankey";
import { buildSankeyGraph } from "./sankeyGraph";
import {
  layoutCountLabels,
  countLabelStyle,
  createCanvasTextMeasurer,
  estimateTextWidthPx,
  maxInlineLabelWidthPx,
  COUNT_LABEL_GAP,
  buildAxisLabels,
  buildLegend,
  buildCaption,
} from "./sankeyLabels";
import {
  computeNodeAbsY,
  computeColumnX,
  columnAnchorX,
  RIBBON_HIT_H,
} from "./sankeyGeometry";
import { flowOpacity } from "./sankeyInteraction";
import {
  computeRequiredWidthPx,
  computeWidthGrowth,
  getPanBounds,
  clampPanValue,
  initialPan,
  wheelPanDelta,
  getEdgeFades,
  arrowPanStep,
  panToReveal,
} from "./sankeyPan";
import { buildFocusOrder, nextFocusIndex } from "./sankeyFocus";
import { nodeTooltipModel, flowTooltipModel } from "./sankeyTooltipModel";
import { SankeyTooltip, TOOLTIP_W } from "./SankeyTooltip";
import {
  CANVAS,
  VIEW_W,
  PLOT_H,
  TYPE,
  SPACING,
  NODE_WIDTH,
  HOST_BOX,
  EDGE_OPACITY,
  MIN_COLUMN_GAP_PX,
  UNKNOWN_BUCKET_COLOR,
  CANVAS_UNITS_PER_PX_Y,
  COLOR,
} from "./sankeyTokens";

const pct = (v, of) => `${(v / of) * 100}%`;

// Stable defaults: a fresh [] per render would rebuild the SVG on every hover.
const EMPTY = Object.freeze([]);

// Pixel movement before a press becomes a drag. Below this it is a click.
const DRAG_THRESHOLD = 4;
// Breathing room so the first/last columns are not clipped while panning.
const EDGE_PAD_PX = 40;

// Safe in an id-like data-testid: spaces and symbols in bucket names become "_".
const testIdPart = (value) => String(value ?? "").replace(/[^A-Za-z0-9_-]+/g, "_");

/** Overlay that fades the chart into the card at an edge with hidden columns. */
const edgeFadeStyle = (side, visible) => ({
  position: "absolute",
  top: 0,
  bottom: 0,
  [side]: 0,
  width: "40px",
  pointerEvents: "none",
  opacity: visible ? 1 : 0,
  transition: "opacity .2s",
  background: `linear-gradient(to ${side === "left" ? "right" : "left"}, rgba(255, 255, 255, 0.95), rgba(255, 255, 255, 0))`,
});

// Keyboard focus outline on a node's rect or a ribbon (theme ink colour).
const FOCUS_STROKE_W = "2";

/** Keys that activate a focused node or ribbon (same as a click). */
export const isActivationKey = (key) => key === "Enter" || key === " " || key === "Spacebar";

/** onFlowClick payload for a ribbon (edgeMeta from buildSankeyGraph). */
export function buildFlowClickPayload(meta, columns, buckets) {
  if (!meta) return null;
  return {
    flowId: meta.id,
    fromColumnKey: columns[meta.fromIndex]?.key,
    toColumnKey: columns[meta.fromIndex + 1]?.key,
    fromBucketId: buckets[meta.fromBucket]?.id,
    toBucketId: buckets[meta.toBucket]?.id,
    fromBucketLabel: buckets[meta.fromBucket]?.label,
    toBucketLabel: buckets[meta.toBucket]?.label,
    count: meta.value,
  };
}

/** Filled ribbon outline between the source column's right edge x1 and the target's left edge x2. */
export function ribbonPath(x1, x2, { yL0, yL1, yR0, yR1 }) {
  const midX = (x1 + x2) / 2;
  return `M${x1},${yL0} C${midX},${yL0} ${midX},${yR0} ${x2},${yR0} L${x2},${yR1} C${midX},${yR1} ${midX},${yL1} ${x1},${yL1} Z`;
}

/** Transparent stroke width that brings a ribbon's hit area up to RIBBON_HIT_H. */
export const ribbonHitStroke = (thickness) => Math.max(0, RIBBON_HIT_H - thickness);

// ---- d3-sankey layout -------------------------------------------------------

/**
 * Runs the d3-sankey layout, then pins every node's x0/x1/y0/y1 to the computed
 * column / bucket slots and calls sankey.update(graph) so link geometry follows.
 */
export function runLayout(graph, columnX, nodeWidth) {
  const sankeyLayout = d3Sankey()
    .nodeId((d) => d.id)
    .nodeAlign(sankeyLeft)
    .nodeWidth(nodeWidth)
    .nodePadding(0)
    .nodeSort(null)
    .extent([
      [SPACING, 0],
      [VIEW_W + SPACING, PLOT_H],
    ]);

  // Clone so d3-sankey can mutate freely without corrupting the memoised graph.
  const sankeyNodes = graph.nodes.map((n) => ({ ...n }));
  const sankeyLinks = graph.edges.map((e) => ({ ...e }));
  const d3Graph = sankeyLayout({ nodes: sankeyNodes, links: sankeyLinks });

  const absY = graph.absY || computeNodeAbsY(graph.distributions);
  d3Graph.nodes.forEach((node) => {
    const meta = graph.nodeMeta.get(node.id);
    if (!meta) return;
    const ay = absY[meta.columnIndex][meta.bucketIndex];
    node.x0 = columnX[meta.columnIndex].x0;
    node.x1 = columnX[meta.columnIndex].x1;
    node.y0 = ay.y0;
    node.y1 = ay.y1;
  });
  sankeyLayout.update(d3Graph);

  return { d3Graph, absY };
}

// ---- SVG renderer -----------------------------------------------------------

/**
 * Renders the laid-out graph into `svgEl` as plain SVG. Node <g> elements carry
 * data-node-id and a data-testid; ribbons carry a data-testid,
 * data-edge-source / data-edge-target.
 */
function renderSankeyToSVG(svgEl, { d3Graph, graph, columnX, columns, buckets, testIdPrefix, clickable, flowsClickable }) {
  while (svgEl.firstChild) svgEl.removeChild(svgEl.firstChild);

  const svgNS = "http://www.w3.org/2000/svg";
  const mk = (tag) => document.createElementNS(svgNS, tag);

  const steps = Math.max(0, columns.length - 1);
  const flowsByStage = Array.from({ length: steps }, () => []);
  graph.edgeMeta.forEach((meta) => {
    flowsByStage[meta.fromIndex]?.push(meta);
  });

  // --- ribbons (drawn first so nodes sit on top) ---
  const edgeLayer = mk("g");
  edgeLayer.setAttribute("class", "sankey-edges");
  const edgeEls = new Map();

  for (let p = 0; p < steps; p++) {
    const stageFlows = flowsByStage[p];
    if (!stageFlows.length) continue;

    stageFlows.forEach((meta) => {
      const ends = graph.ribbons?.get(meta.id);
      if (!ends) return;
      const from = buckets[meta.fromBucket];
      const to = buckets[meta.toBucket];
      const path = mk("path");
      path.setAttribute("data-edge-source", meta.source);
      path.setAttribute("data-edge-target", meta.target);
      path.setAttribute(
        "data-testid",
        `${testIdPrefix}-flow-${testIdPart(columns[p].key)}-${testIdPart(buckets[meta.fromBucket]?.id)}-${testIdPart(buckets[meta.toBucket]?.id)}`
      );
      path.setAttribute("fill", from?.color || UNKNOWN_BUCKET_COLOR);
      // Invisible hit area: a transparent stroke tops a thin ribbon up to RIBBON_HIT_H.
      const hitStroke = ribbonHitStroke(ends.yL1 - ends.yL0);
      if (hitStroke > 0) {
        path.setAttribute("stroke", "transparent");
        path.setAttribute("stroke-width", String(hitStroke));
        path.setAttribute("stroke-linejoin", "round");
      }
      path.setAttribute("pointer-events", "all");
      path.setAttribute(
        "aria-label",
        `${from?.label} to ${to?.label}, ${columns[p]?.label} to ${columns[p + 1]?.label}: ${meta.value}`
      );
      if (flowsClickable) {
        path.setAttribute("role", "button");
        // Roving tabindex: the component makes the active item 0 (sankeyFocus.js).
        path.setAttribute("tabindex", "-1");
        path.style.outline = "none";
      }
      path.style.opacity = String(EDGE_OPACITY);
      path.style.cursor = flowsClickable ? "pointer" : "default";
      path.style.transition = "opacity .15s";
      path.setAttribute("d", ribbonPath(columnX[p].x1, columnX[p + 1].x0, ends));
      edgeLayer.appendChild(path);
      edgeEls.set(meta.id, path);
    });
  }
  svgEl.appendChild(edgeLayer);

  // --- nodes ---
  const nodeLayer = mk("g");
  nodeLayer.setAttribute("class", "sankey-nodes");
  const nodeEls = new Map();

  d3Graph.nodes.forEach((node) => {
    const meta = graph.nodeMeta.get(node.id);
    if (!meta) return;
    const g = mk("g");
    g.setAttribute("data-node-id", node.id);
    g.setAttribute("data-testid", `${testIdPrefix}-node-${testIdPart(meta.columnKey)}-${testIdPart(meta.bucketId)}`);
    g.setAttribute("data-column-key", String(meta.columnKey));
    g.setAttribute("data-bucket-id", String(meta.bucketId));
    g.setAttribute("aria-label", `${buckets[meta.bucketIndex]?.label}, ${columns[meta.columnIndex]?.label}: ${meta.count}`);
    if (clickable) {
      g.setAttribute("role", "button");
      g.setAttribute("tabindex", "-1");
      g.style.outline = "none";
    }
    g.style.cursor = clickable ? "pointer" : "default";

    const rect = mk("rect");
    const h = node.y1 - node.y0;
    rect.setAttribute("x", String(node.x0));
    rect.setAttribute("y", String(node.y0));
    rect.setAttribute("width", String(node.x1 - node.x0));
    rect.setAttribute("height", String(h));
    rect.setAttribute("fill", node.color || UNKNOWN_BUCKET_COLOR);
    rect.setAttribute("rx", "2");

    g.appendChild(rect);
    nodeLayer.appendChild(g);
    nodeEls.set(node.id, g);
  });
  svgEl.appendChild(nodeLayer);

  return { nodeEls, edgeEls };
}

// ---- React component --------------------------------------------------------

/**
 * Sankey of how items move between buckets from one column (period) to the next.
 *
 * @param {Object} props
 * @param {{key:string,label:string}[]} props.columns   columns in time order
 * @param {{id:string,label:string,color:string}[]} props.buckets  top -> bottom order (also the legend)
 * @param {{columnKey:string,bucketId:string,count:number}[]} props.nodes  node size per (column, bucket)
 * @param {{fromColumnKey:string,toColumnKey:string,fromBucketId:string,toBucketId:string,count:number}[]} props.flows
 *        ribbons between ADJACENT columns
 * @param {string} [props.nounSingular]   e.g. "battery"
 * @param {string} [props.nounPlural]     e.g. "batteries"
 * @param {string} [props.columnNoun]     what a column is, for the caption, e.g. "month"
 * @param {string|null} [props.caption]   overrides the caption; null hides it
 * @param {number} [props.dimOpacity]     ribbon opacity factor while something else is hovered
 * @param {number} [props.minColumnWidthPx] minimum distance between adjacent column centres on
 *        screen (default MIN_COLUMN_GAP_PX); more columns than fit make the chart pannable
 * @param {(ctx: {nodeId:string, columnIndex:number, columnKey:string, bucketId:string, bucketLabel:string, count:number}) => void} [props.onNodeClick]
 * @param {(ctx: {flowId:string, fromColumnKey:string, toColumnKey:string, fromBucketId:string, toBucketId:string, fromBucketLabel:string, toBucketLabel:string, count:number}) => void} [props.onFlowClick]
 *        click (or Enter / Space) on a ribbon
 * @param {string} [props.dataTestId]     data-testid of the container; node/flow test ids are prefixed with it
 */
export function BucketSankeyChart({
  columns = EMPTY,
  buckets = EMPTY,
  nodes = EMPTY,
  flows = EMPTY,
  nounSingular = "item",
  nounPlural = "items",
  columnNoun = "column",
  caption = undefined,
  dimOpacity = 0.15,
  minColumnWidthPx = MIN_COLUMN_GAP_PX,
  onNodeClick = undefined,
  onFlowClick = undefined,
  dataTestId = "bucket-sankey-chart",
}) {
  const outerRef = useRef(null);
  const boxRef = useRef(null);
  const svgRef = useRef(null);
  const viewportRef = useRef(null);
  const domRef = useRef({ nodeEls: new Map(), edgeEls: new Map() });
  const tipModelRef = useRef(null);

  const [hover, setHover] = useState(null); // { kind: 'node'|'edge', id }
  const [tip, setTip] = useState(null);     // { x, y, w, model } relative to outerRef
  // Measured viewport (card) width, px. Drives widthGrowth and the label sizes.
  const [viewportWidthPx, setViewportWidthPx] = useState(0);
  const [panX, setPanX] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [viewportFocused, setViewportFocused] = useState(false);
  // Node / ribbon id that holds the chart's single tab stop; kept across rebuilds.
  const activeFocusIdRef = useRef(null);

  // Drag-to-pan state in refs, so a drag does not re-render on every move.
  const dragRef = useRef({ down: false, dragging: false, startX: 0, startPan: 0, pointerId: null });

  const safeColumns = Array.isArray(columns) ? columns : EMPTY;
  const safeBuckets = Array.isArray(buckets) ? buckets : EMPTY;
  const clickable = typeof onNodeClick === "function";
  const flowsClickable = typeof onFlowClick === "function";

  const graph = useMemo(
    () => buildSankeyGraph({ columns: safeColumns, buckets: safeBuckets, nodes, flows }),
    [safeColumns, safeBuckets, nodes, flows]
  );

  // Real text widths in the browser (app font); estimates where there is no canvas.
  const measureText = useMemo(() => {
    const family = typeof window !== "undefined" && document?.body
      ? window.getComputedStyle(document.body).fontFamily
      : undefined;
    return createCanvasTextMeasurer(family || "sans-serif") || estimateTextWidthPx;
  }, []);

  // The box is widthGrowth x the viewport: 1 when the columns fit with at least
  // minColumnWidthPx between them (and the last column's labels fit), else wider and
  // pannable.
  const requiredWidthPx = useMemo(
    () => computeRequiredWidthPx({
      columnCount: safeColumns.length,
      minColumnGapPx: minColumnWidthPx,
      lastLabelWidthPx: maxInlineLabelWidthPx(graph, measureText),
    }),
    [safeColumns.length, minColumnWidthPx, graph, measureText]
  );
  const widthGrowth = computeWidthGrowth(requiredWidthPx, viewportWidthPx);
  const boxWidthPx = viewportWidthPx * widthGrowth;
  // Node width is pre-divided by widthGrowth so the rects render NODE_WIDTH px wide
  // after the horizontal stretch; only the gaps between columns get wider.
  const nodeWidth = NODE_WIDTH / widthGrowth;
  const columnX = useMemo(() => computeColumnX(safeColumns.length, nodeWidth), [safeColumns.length, nodeWidth]);
  const colAnchors = useMemo(() => columnAnchorX(columnX), [columnX]);

  const countLayout = useMemo(
    () => layoutCountLabels(graph, columnX, {
      boxWidthPx,
      // The box height is fixed in px, independent of the width.
      verticalUnitsPerPx: CANVAS_UNITS_PER_PX_Y,
      measureText,
    }),
    [graph, columnX, boxWidthPx, measureText]
  );
  const maxLabelWidthPx = countLayout.labels.reduce((m, l) => Math.max(m, l.widthPx || 0), 0);

  // ---- pan bounds -----------------------------------------------------------
  // Content: first column's left edge .. last column's labels' right edge (px from
  // the box's left edge).
  const panBounds = useMemo(() => {
    if (!colAnchors.length || !(boxWidthPx > 0)) return { minPan: 0, maxPan: 0, pannable: false };
    const pxPerUnit = boxWidthPx / CANVAS.W;
    const contentL = (colAnchors[0] - CANVAS.nodeLeftOfCentre) * pxPerUnit;
    const contentR = (colAnchors[colAnchors.length - 1] - CANVAS.nodeLeftOfCentre + nodeWidth + COUNT_LABEL_GAP)
      * pxPerUnit + maxLabelWidthPx;
    return getPanBounds({ viewportW: viewportWidthPx, boxW: boxWidthPx, contentL, contentR, edgePad: EDGE_PAD_PX });
  }, [colAnchors, boxWidthPx, viewportWidthPx, nodeWidth, maxLabelWidthPx]);
  const canPan = panBounds.pannable;
  const columnPitchPx = columnX.length > 1 ? (columnX[1].x0 - columnX[0].x0) * (boxWidthPx / CANVAS.W) : 0;

  // Start at the right end (latest columns) whenever the layout changes; on a plain
  // resize keep the position, clamped.
  const panLayoutKeyRef = useRef("");
  useEffect(() => {
    const key = `${safeColumns.map((c) => c.key).join("|")}#${widthGrowth > 1}`;
    if (key !== panLayoutKeyRef.current) {
      panLayoutKeyRef.current = key;
      setPanX(initialPan(panBounds));
    } else {
      setPanX((prev) => clampPanValue(prev, panBounds));
    }
  }, [panBounds, safeColumns, widthGrowth]);

  const panRef = useRef({ panX, panBounds, canPan });
  panRef.current = { panX, panBounds, canPan };
  // Latest geometry for revealing a focused column without rebuilding the SVG.
  const revealRef = useRef({});
  revealRef.current = { colAnchors, boxWidthPx, viewportWidthPx };
  const hasFocusableItems = clickable || flowsClickable;

  // Measure the viewport (card) width.
  useEffect(() => {
    const viewport = viewportRef.current;
    if (!viewport) return undefined;
    const read = () => setViewportWidthPx(viewport.clientWidth || viewport.getBoundingClientRect().width || 0);
    read();
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(read) : null;
    ro?.observe(viewport);
    return () => ro?.disconnect();
  }, [safeColumns.length]);

  // ---- drag-to-pan handlers (mouse and touch, via pointer events) ------------
  // Pointer capture is only taken once the pointer has moved past DRAG_THRESHOLD,
  // so plain clicks/hover still reach the SVG nodes and ribbons.
  const onPointerDown = useCallback((e) => {
    if (e.button !== 0 || !canPan) return;
    dragRef.current = { down: true, dragging: false, startX: e.clientX, startPan: panX, pointerId: e.pointerId };
  }, [panX, canPan]);

  const onPointerMove = useCallback((e) => {
    const d = dragRef.current;
    if (!d.down) return;
    const delta = e.clientX - d.startX;
    if (!d.dragging) {
      if (Math.abs(delta) < DRAG_THRESHOLD) return;
      d.dragging = true;
      setIsDragging(true);
      // No tooltip while dragging.
      tipModelRef.current = null;
      setTip(null);
      setHover(null);
      try { e.currentTarget.setPointerCapture(d.pointerId); } catch { /* pointer gone */ }
    }
    setPanX(clampPanValue(d.startPan + delta, panRef.current.panBounds));
  }, []);

  const onPointerUp = useCallback((e) => {
    const d = dragRef.current;
    if (d.dragging) {
      try { e.currentTarget.releasePointerCapture(d.pointerId); } catch { /* already released */ }
      setIsDragging(false);
    }
    d.down = false;
    // Keep `dragging` set until the click that follows pointerup is swallowed.
    setTimeout(() => { dragRef.current.dragging = false; }, 0);
  }, []);

  // A drag that ends over a node or ribbon must not also count as a click on it.
  const onClickCapture = useCallback((e) => {
    if (dragRef.current.dragging) {
      e.stopPropagation();
      e.preventDefault();
    }
  }, []);

  // Arrow keys pan by one column while the chart (or a node / ribbon in it) has focus.
  const onViewportKeyDown = useCallback((e) => {
    if (!canPan) return;
    const step = arrowPanStep(e.key, columnPitchPx);
    if (!step) return;
    e.preventDefault();
    setPanX((prev) => clampPanValue(prev + step, panBounds));
  }, [canPan, columnPitchPx, panBounds]);

  // Trackpad horizontal swipe / shift + wheel pans. A plain vertical wheel is left to
  // the page. Native listener: React's wheel handler is passive and cannot
  // preventDefault.
  useEffect(() => {
    const viewport = viewportRef.current;
    if (!viewport) return undefined;
    const onWheel = (evt) => {
      const { panX: current, panBounds: bounds, canPan: pannable } = panRef.current;
      if (!pannable) return;
      const dx = wheelPanDelta(evt, viewport.clientWidth);
      if (!dx) return;
      const next = clampPanValue(current - dx, bounds);
      if (next === current) return; // at an end: let the browser have it
      evt.preventDefault();
      setPanX(next);
    };
    viewport.addEventListener("wheel", onWheel, { passive: false });
    // Focusing an item in a hidden column makes the browser scroll this
    // overflow-hidden viewport, on top of translateX. Undo it; panning is done by
    // panToReveal instead.
    const onScroll = () => {
      if (viewport.scrollLeft !== 0) viewport.scrollLeft = 0;
    };
    viewport.addEventListener("scroll", onScroll);
    return () => {
      viewport.removeEventListener("wheel", onWheel);
      viewport.removeEventListener("scroll", onScroll);
    };
  }, [safeColumns.length]);

  const fades = getEdgeFades(panX, panBounds);

  // Latest-value refs so the SVG is not rebuilt when only hover state changes.
  const onNodeClickRef = useRef(onNodeClick);
  onNodeClickRef.current = onNodeClick;
  const onFlowClickRef = useRef(onFlowClick);
  onFlowClickRef.current = onFlowClick;
  const styleStateRef = useRef({});
  styleStateRef.current = { hover, dimOpacity };

  /** Restyles the ribbons from the hover rule. */
  const applyHighlight = useCallback(() => {
    const ctx = styleStateRef.current;
    domRef.current.edgeEls.forEach((el, id) => {
      const meta = graph.edgeMeta.get(id);
      if (!meta) return;
      // Ribbons rest at EDGE_OPACITY, never CSS 1.
      el.style.opacity = String(EDGE_OPACITY * flowOpacity(meta, ctx));
    });
  }, [graph]);

  // ---- build / rebuild the SVG when data changes ---------------------------
  useEffect(() => {
    const svgEl = svgRef.current;
    const box = boxRef.current;
    if (!svgEl || !box || graph.nodes.length === 0) return undefined;

    const { d3Graph } = runLayout(graph, columnX, nodeWidth);
    const { nodeEls, edgeEls } = renderSankeyToSVG(svgEl, {
      d3Graph,
      graph,
      columnX,
      columns: safeColumns,
      buckets: safeBuckets,
      testIdPrefix: dataTestId,
      clickable,
      flowsClickable,
    });
    domRef.current = { nodeEls, edgeEls };
    applyHighlight();

    // ---- keyboard focus (roving tabindex) ----------------------------------
    const focusOrder = buildFocusOrder(graph, { nodes: clickable, flows: flowsClickable });
    const elOf = (item) => (item.kind === "node" ? nodeEls.get(item.id) : edgeEls.get(item.id));
    const setActive = (id) => {
      focusOrder.forEach((item) => elOf(item)?.setAttribute("tabindex", item.id === id ? "0" : "-1"));
      activeFocusIdRef.current = id;
    };
    if (focusOrder.length) {
      const keep = focusOrder.some((item) => item.id === activeFocusIdRef.current);
      setActive(keep ? activeFocusIdRef.current : focusOrder[0].id);
    }
    // Outline on the node's rect, or on the ribbon itself (its hit stroke is saved and
    // restored on blur).
    const setFocusStyle = (item, on) => {
      const el = elOf(item);
      const target = item.kind === "node" ? el?.firstChild : el;
      if (!target) return;
      if (on) {
        target.dataset.prevStroke = target.getAttribute("stroke") ?? "";
        target.dataset.prevStrokeWidth = target.getAttribute("stroke-width") ?? "";
        target.setAttribute("stroke", COLOR.ink);
        target.setAttribute("stroke-width", FOCUS_STROKE_W);
      } else {
        ["stroke", "stroke-width"].forEach((attr) => {
          const prev = target.dataset[attr === "stroke" ? "prevStroke" : "prevStrokeWidth"];
          if (prev) target.setAttribute(attr, prev);
          else target.removeAttribute(attr);
        });
      }
    };
    const revealColumn = (columnIndex) => {
      const { panX: current, panBounds: bounds, canPan: pannable } = panRef.current;
      const { colAnchors: anchors, boxWidthPx: boxW, viewportWidthPx: viewW } = revealRef.current;
      if (!pannable || anchors?.[columnIndex] == null) return;
      const xPx = (anchors[columnIndex] * boxW) / CANVAS.W;
      const next = panToReveal({ panX: current, xPx, viewportW: viewW, bounds, marginPx: EDGE_PAD_PX * 2 });
      if (next !== current) setPanX(next);
    };
    const focusHandlers = focusOrder.map((item, index) => {
      const el = elOf(item);
      const onFocus = () => {
        setActive(item.id);
        setFocusStyle(item, true);
        revealColumn(item.columnIndex);
      };
      const onBlur = () => setFocusStyle(item, false);
      // Arrows move focus here; stopPropagation keeps the viewport from also panning.
      const onArrow = (evt) => {
        const next = nextFocusIndex(evt.key, index, focusOrder.length);
        if (next === null) return;
        evt.preventDefault();
        evt.stopPropagation();
        elOf(focusOrder[next])?.focus({ preventScroll: true });
      };
      el?.addEventListener("focus", onFocus);
      el?.addEventListener("blur", onBlur);
      el?.addEventListener("keydown", onArrow);
      return { el, onFocus, onBlur, onArrow };
    });

    // ---- event handlers ---------------------------------------------------
    const outer = outerRef.current;
    const tipCtx = { graph, columns: safeColumns, buckets: safeBuckets, nounSingular, nounPlural };
    const tipPos = (evt, model) => {
      const r = outer.getBoundingClientRect();
      return { x: evt.clientX - r.left, y: evt.clientY - r.top, w: r.width, model };
    };
    const showTip = (model, evt) => {
      if (dragRef.current.dragging) return;
      tipModelRef.current = model;
      setTip(tipPos(evt, model));
    };
    const clearHover = () => {
      tipModelRef.current = null;
      setHover(null);
      setTip(null);
    };
    const onMove = (evt) => {
      if (!tipModelRef.current) return;
      if (dragRef.current.dragging) { clearHover(); return; }
      setTip(tipPos(evt, tipModelRef.current));
    };

    const nodeHandlers = [];
    nodeEls.forEach((el, id) => {
      const onEnter = (evt) => {
        const meta = graph.nodeMeta.get(id);
        if (!meta) return;
        setHover({ kind: "node", id });
        showTip(nodeTooltipModel(meta, { ...tipCtx, clickable: !!onNodeClickRef.current }), evt);
      };
      const onLeave = () => clearHover();
      const onClick = () => {
        const meta = graph.nodeMeta.get(id);
        if (!meta) return;
        onNodeClickRef.current?.({
          nodeId: id,
          columnIndex: meta.columnIndex,
          columnKey: meta.columnKey,
          bucketId: meta.bucketId,
          bucketLabel: safeBuckets[meta.bucketIndex]?.label,
          count: meta.count,
        });
      };
      // Keyboard: Enter / Space on a focused node opens it like a click.
      const onKeyDown = (evt) => {
        if (!isActivationKey(evt.key)) return;
        evt.preventDefault();
        onClick();
      };
      el.addEventListener("mouseenter", onEnter);
      el.addEventListener("mouseleave", onLeave);
      el.addEventListener("click", onClick);
      el.addEventListener("keydown", onKeyDown);
      nodeHandlers.push({ el, onEnter, onLeave, onClick, onKeyDown });
    });

    const edgeHandlers = [];
    edgeEls.forEach((el, id) => {
      const onEnter = (evt) => {
        const meta = graph.edgeMeta.get(id);
        if (!meta) return;
        setHover({ kind: "edge", id });
        showTip(flowTooltipModel(meta, { ...tipCtx, clickable: !!onFlowClickRef.current }), evt);
      };
      const onLeave = () => clearHover();
      const onClick = () => {
        const payload = buildFlowClickPayload(graph.edgeMeta.get(id), safeColumns, safeBuckets);
        if (payload) onFlowClickRef.current?.(payload);
      };
      const onKeyDown = (evt) => {
        if (!isActivationKey(evt.key)) return;
        evt.preventDefault();
        onClick();
      };
      el.addEventListener("mouseenter", onEnter);
      el.addEventListener("mouseleave", onLeave);
      el.addEventListener("click", onClick);
      el.addEventListener("keydown", onKeyDown);
      edgeHandlers.push({ el, onEnter, onLeave, onClick, onKeyDown });
    });

    svgEl.addEventListener("mousemove", onMove);

    return () => {
      focusHandlers.forEach(({ el, onFocus, onBlur, onArrow }) => {
        el?.removeEventListener("focus", onFocus);
        el?.removeEventListener("blur", onBlur);
        el?.removeEventListener("keydown", onArrow);
      });
      svgEl.removeEventListener("mousemove", onMove);
      nodeHandlers.forEach(({ el, onEnter, onLeave, onClick, onKeyDown }) => {
        el.removeEventListener("mouseenter", onEnter);
        el.removeEventListener("mouseleave", onLeave);
        el.removeEventListener("click", onClick);
        el.removeEventListener("keydown", onKeyDown);
      });
      edgeHandlers.forEach(({ el, onEnter, onLeave, onClick, onKeyDown }) => {
        el.removeEventListener("mouseenter", onEnter);
        el.removeEventListener("mouseleave", onLeave);
        el.removeEventListener("click", onClick);
        el.removeEventListener("keydown", onKeyDown);
      });
      while (svgEl.firstChild) svgEl.removeChild(svgEl.firstChild);
      domRef.current = { nodeEls: new Map(), edgeEls: new Map() };
      tipModelRef.current = null;
      setHover(null);
      setTip(null);
    };
  }, [graph, columnX, nodeWidth, safeColumns, safeBuckets, nounSingular, nounPlural, applyHighlight, dataTestId, clickable, flowsClickable]);

  // ---- hover styling (no rebuild) ------------------------------------------
  useEffect(() => {
    applyHighlight();
  }, [hover, dimOpacity, applyHighlight]);

  // ---- overlay labels -----------------------------------------------------
  const axisLabels = useMemo(() => buildAxisLabels(safeColumns, colAnchors), [safeColumns, colAnchors]);
  const legend = useMemo(() => buildLegend(safeBuckets), [safeBuckets]);
  const captionModel = useMemo(
    () => buildCaption({ columnNoun, nounSingular, nounPlural, clickable, flowsClickable }),
    [columnNoun, nounSingular, nounPlural, clickable, flowsClickable]
  );
  const captionText = caption === undefined ? captionModel.text : caption;

  if (safeColumns.length === 0) return null;

  return (
    <div ref={outerRef} data-testid={dataTestId} style={{ position: "relative", width: "100%", minWidth: 0 }}>
      {/* Legend: above the plot */}
      <div style={legend.rowStyle} data-testid={`${dataTestId}-legend`}>
        {legend.items.map((item) => (
          <span key={item.key} style={item.itemStyle}>
            <span style={item.dotStyle} />
            <span style={item.textStyle}>{item.label}</span>
          </span>
        ))}
      </div>
      {/* Chart viewport: clips and pans, never scrolls the page */}
      <div
        ref={viewportRef}
        data-testid={`${dataTestId}-viewport`}
        data-pannable={canPan ? "true" : "false"}
        // Its own tab stop only when it has no nodes / ribbons to focus; otherwise the
        // chart's single stop is the active item, which pans its column into view.
        tabIndex={canPan && !hasFocusableItems ? 0 : undefined}
        aria-label={canPan ? "Chart: drag, or use the arrow keys, to see more months" : undefined}
        onFocus={(e) => { if (e.target === e.currentTarget) setViewportFocused(true); }}
        onBlur={(e) => { if (e.target === e.currentTarget) setViewportFocused(false); }}
        style={{
          position: "relative",
          overflow: "hidden",
          cursor: canPan ? (isDragging ? "grabbing" : "grab") : "default",
          // Vertical touch scrolling stays with the page; horizontal drags pan.
          touchAction: canPan ? "pan-y" : "auto",
          userSelect: "none",
          // The native ring is replaced by an inset ring in the theme ink colour.
          outline: "none",
          boxShadow: viewportFocused ? `inset 0 0 0 2px ${COLOR.ink}` : undefined,
          borderRadius: "4px",
        }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        onClickCapture={onClickCapture}
        onKeyDown={onViewportKeyDown}
      >
        {/* The CANVAS.W-unit-wide, CANVAS.H-px-tall box, panned by translateX */}
        <div
          ref={boxRef}
          style={{
            position: "relative",
            width: `${widthGrowth * 100}%`,
            // Fixed height (1 vertical canvas unit = 1px): the chart no longer grows
            // taller with the card width.
            height: `${CANVAS.H}px`,
            transform: canPan ? `translateX(${panX}px)` : undefined,
            willChange: canPan ? "transform" : undefined,
          }}
        >
          <div
            style={{
              position: "absolute",
              left: pct(HOST_BOX.left, CANVAS.W),
              top: pct(HOST_BOX.top, CANVAS.H),
              width: pct(HOST_BOX.width, CANVAS.W),
              height: pct(HOST_BOX.height, CANVAS.H),
            }}
          >
            <svg
              ref={svgRef}
              viewBox={`0 0 ${VIEW_W + 2 * SPACING} ${PLOT_H}`}
              preserveAspectRatio="none"
              style={{ width: "100%", height: "100%", overflow: "visible", display: "block" }}
            />
          </div>

          {/* Count + axis labels (pan with the chart) */}
          <div style={{ position: "absolute", inset: 0, pointerEvents: "none" }}>
            {axisLabels.map((l) => (
              <span key={l.key} style={l.style}>
                {l.text}
              </span>
            ))}
            {countLayout.labels.map((l) => (
              <span
                key={l.key}
                style={countLabelStyle(l, countLayout.mode)}
                data-testid={`${dataTestId}-count-${l.nodeId}`}
              >
                <span style={TYPE.countLabel}>{l.countText}</span>
                {l.shareText && (
                  <span style={TYPE.countShare}>
                    {countLayout.mode === "inline" ? `(${l.shareText})` : l.shareText}
                  </span>
                )}
              </span>
            ))}
          </div>
        </div>

        {/* Edge fades where more columns are hidden (overlays: no layout shift) */}
        <div aria-hidden="true" data-testid={`${dataTestId}-fade-left`} style={edgeFadeStyle("left", fades.left)} />
        <div aria-hidden="true" data-testid={`${dataTestId}-fade-right`} style={edgeFadeStyle("right", fades.right)} />
      </div>

      {/* Tooltip lives on the outer wrapper (not the clipped viewport) so it can
          overflow the chart and flip near the edges. */}
      {tip && (() => {
        const half = TOOLTIP_W / 2 + 8;
        const left = Math.max(half, Math.min(tip.w - half, tip.x));
        const below = tip.y < 190;
        return (
          <div
            style={{
              position: "absolute",
              left: `${left}px`,
              top: `${tip.y}px`,
              transform: below ? "translate(-50%, 16px)" : "translate(-50%, calc(-100% - 14px))",
              zIndex: 10,
              pointerEvents: "none",
            }}
          >
            <SankeyTooltip model={tip.model} />
          </div>
        );
      })()}

      {/* Caption: one muted line under the month axis */}
      {captionText && <div style={captionModel.style}>{captionText}</div>}
    </div>
  );
}

export default BucketSankeyChart;
